import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { distinctUntilChanged, startWith } from 'rxjs';
import { Question } from 'src/app/core/models/questionnaire.model';
import { cpfValidator } from '../../../utils/cpf.validator';
import { QuestionAnswer } from '../question/question.component';
import { DynamicService } from 'src/app/core/services/base/dynamic.service';
import { UserService } from 'src/app/core/services/user.service';
import { AutofillFilter } from '../question-text/question-text.component';
import { KycFieldLockService } from 'src/app/core/services/kyc-field-lock.service';

@Component({
  selector: 'app-question-cpf',
  templateUrl: './question-cpf.component.html',
  styleUrls: ['./question-cpf.component.scss'],
})
export class QuestionCpfComponent implements OnInit {
  @Input('question') question!: Question;
  @Input() user: any;
  @Output('changeAnswer') changeAnswer: EventEmitter<QuestionAnswer> =
    new EventEmitter();

  cpfControl = new FormControl('', [cpfValidator]);
  showErrors = false;
  autofillMode = false;
  autofillEditable = false;
  kycLocked = false;
  inputMode: string = 'numeric';
  autocomplete: string | null = null;

  private readonly validInputModes = ['none', 'text', 'decimal', 'numeric', 'tel', 'search', 'email', 'url'];

  constructor(
    private dynamicService: DynamicService<any>,
    private userService: UserService,
    private kycFieldLock: KycFieldLockService
  ) {}

  async ngOnInit(): Promise<void> {
    if (!this.question.elective) {
      this.cpfControl.addValidators([Validators.required]);
    }

    const configInputMode = this.question.configuration?.inputmode;
    if (configInputMode && this.validInputModes.includes(configInputMode)) {
      this.inputMode = configInputMode;
    }
    const configAutocomplete = this.question.configuration?.autocomplete;
    if (configAutocomplete) {
      this.autocomplete = configAutocomplete;
    }

    const locked = await this.applyKycLock();
    if (!locked && this.question.configuration?.autofill && !this.question.answer?.value) {
      await this.processAutofill(this.question.configuration.autofill);
    }

    if (this.question.answer?.value) {
      this.cpfControl.setValue(this.question.answer.value.toString());
    }
    if (this.kycLocked) {
      this.cpfControl.disable({ emitEvent: false });
    }

    this.cpfControl.valueChanges
      .pipe(
        startWith(''),
        distinctUntilChanged((prev: string | null, curr: string | null) => {
          return prev === curr;
        })
      )
      .subscribe(() => {
        this.showErrors = false;
        this.change();
      });
  }

  /**
   * CPK-5059: com fillInfo.lockWhenKycPassed e KYC aprovado, mostra o CPF do cadastro
   * (mesmo sobre um rascunho) e trava o campo. Vence o autofill.editable.
   */
  private async applyKycLock(): Promise<boolean> {
    if (!KycFieldLockService.lockedField(this.question)) return false;

    const lock = await this.kycFieldLock.resolve(this.question, await this.resolveUserId());
    if (!lock.locked) return false;

    this.kycLocked = true;
    this.question.answer = { ...this.question.answer, value: lock.value };
    return true;
  }

  private async resolveUserId(): Promise<string> {
    if (this.user) return this.user.id;
    const currentUser = await this.userService.getCurrentUser();
    return currentUser?.id || '';
  }

  private async processAutofill(autofillConfig: any): Promise<void> {
    this.autofillMode = true;
    this.autofillEditable = autofillConfig.editable ?? false;

    const userId = await this.resolveUserId();

    const filters: AutofillFilter[] = (autofillConfig.filters || []).map(
      (f: AutofillFilter) => ({ ...f, val: f.val.replace('$userId', userId) })
    );

    const autofillValue = await this.fetchAutofillValue(
      autofillConfig.from,
      autofillConfig.select,
      filters
    );

    if (autofillValue) {
      this.question.answer = { value: autofillValue };
    }
  }

  private async fetchAutofillValue(
    from: string,
    select: string,
    filters: AutofillFilter[]
  ): Promise<string | null> {
    try {
      this.dynamicService.load(from);
      const result = await this.dynamicService.autofillQuery(from, select, filters);

      const splitParts = (select as any).split('->>');
      const lastPart = splitParts[splitParts.length - 1];
      const cleanedLastPart = lastPart.replace(/['"]/g, '');

      return (
        result?.[0]?.[cleanedLastPart] ||
        result?.[0]?.[lastPart] ||
        result?.[0]?.[select as any] ||
        null
      );
    } catch (error) {
      console.error('Error fetching autofill value for CPF:', error);
      return null;
    }
  }

  change() {
    const value = this.cpfControl.value;
    if (this.question.answer) {
      this.question.answer.value = value;
    }
    // Controle desabilitado tem valid=false no Angular; o CPF travado vem do cadastro com KYC aprovado.
    this.question.isValid = this.kycLocked || this.cpfControl.valid;
    this.changeAnswer.next({
      answer: { value: value || '' },
      question: this.question,
    });
  }

  onBlur() {
    this.showErrors = true;
  }
}
